// src/components/DashboardAdmin/ICTData/DomainManager.jsx
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchDomains,
  insertDomain,
  deleteDomain,
  updateDomain,
} from "../../../store/domainsSlice";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../../Styles/Common.css";
const user = JSON.parse(localStorage.getItem("user"));
const userRole = user?.role || null;
const isReadOnly = userRole === "sales";

const DomainsManager = () => {
  const dispatch = useDispatch();
  const { domains, loading, error } = useSelector((state) => state.domains);

  const [editDomain, setEditDomain] = useState(null);
  const [newDomain, setNewDomain] = useState({
    domain: "",
    domainDesc: "",
    icon: "",         // NEW FIELD
    status: "A",
  });
  const [showInsertForm, setShowInsertForm] = useState(false);

  const [editedValues, setEditedValues] = useState({
    domain: "",
    domainDesc: "",
    icon: "",         // NEW FIELD
    status: "A",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("domain");

  useEffect(() => {
    dispatch(fetchDomains());
  }, [dispatch]);

  // For deleting a domain
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this domain?")) {
      dispatch(deleteDomain(id))
        .then(() => toast.success("Domain deleted successfully!"))
        .catch(() => toast.error("Failed to delete the domain."));
    }
  };

  // For editing a domain (switch to edit mode)
  const handleEdit = (domain) => {
    setEditDomain(domain.id);
    setEditedValues({
      domain: domain.domain,
      domainDesc: domain.domainDesc,
      icon: domain.icon || "",    // Possibly empty string if not set
      status: domain.status,
    });
  };

  // For handling typed changes in the edit form
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditedValues((prevValues) => ({ ...prevValues, [name]: value }));
  };

  // Actually updating the domain in the store
  const handleUpdate = (e) => {
    e.preventDefault();
    dispatch(
      updateDomain({
        id: editDomain,
        ...editedValues,
      })
    )
      .then(() => {
        toast.success("Domain updated successfully!");
        setEditDomain(null); // Close edit mode
      })
      .catch(() => toast.error("Failed to update the domain."));
  };

  // For the Insert form changes
  const handleInsertChange = (e) => {
    const { name, value } = e.target;
    setNewDomain((prevValues) => ({ ...prevValues, [name]: value }));
  };

  // Actually inserting a new domain
  const handleInsert = (e) => {
    e.preventDefault();
    dispatch(insertDomain(newDomain))
      .then(() => {
        toast.success("Domain added successfully!");
        setShowInsertForm(false); // Close insert form
        setNewDomain({ domain: "", domainDesc: "", icon: "", status: "A" }); // Reset form
      })
      .catch(() => toast.error("Failed to add domain."));
  };

  // Toggling domain status from "A" to "I" or vice versa
  const toggleStatus = (domain) => {
    const updatedStatus = domain.status === "A" ? "I" : "A";
    dispatch(updateDomain({ ...domain, status: updatedStatus }))
      .then(() =>
        toast.success(
          `Domain status updated to ${
            updatedStatus === "A" ? "Active" : "Inactive"
          }`
        )
      )
      .catch(() => toast.error("Failed to update domain status."));
  };

  // **Filter and Sort Domains**
  const filteredDomains = domains
    .filter((domain) =>
      domain.domain.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === "domain") {
        return a.domain.localeCompare(b.domain);
      }
      return 0; // Just default for now
    });

  return (
    <div className="container">
      <ToastContainer position="bottom-right" autoClose={3000} />
      <div className="header-container">
        <h2>Domains Manager</h2>
        {!isReadOnly && (
          <button className="add-lead-btn" onClick={() => setShowInsertForm(true)}>
            <i className="fa fa-plus"></i>
          </button>
        )}
      </div>

      {/* Search and Sort Options */}
      <div className="feedback-filters">
        <input
          type="text"
          placeholder="Search Domain"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="filter-input"
        />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="filter-input"
        >
          <option value="domain">Sort by Domain</option>
        </select>
      </div>

      {/* Insert Form as Popup */}
      {showInsertForm && !isReadOnly &&(
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>Add New Domain</h3>
            <form onSubmit={handleInsert} className="insert-form">
              <div className="form-group">
                <label>Domain</label>
                <input
                  type="text"
                  name="domain"
                  value={newDomain.domain}
                  onChange={handleInsertChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  name="domainDesc"
                  value={newDomain.domainDesc}
                  onChange={handleInsertChange}
                  required
                />
              </div>
              {/* NEW: Icon field */}
              <div className="form-group">
                <label>Icon</label>
                <input
                  type="text"
                  name="icon"
                  value={newDomain.icon}
                  onChange={handleInsertChange}
                  placeholder="e.g. fa fa-laptop"
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={newDomain.status}
                  onChange={handleInsertChange}
                >
                  <option value="A">Active</option>
                  <option value="I">Inactive</option>
                </select>
              </div>
              <div className="form-actions">
                <button
                  type="submit"
                  className="action-btn save-btn fa fa-plus-square"
                ></button>
                <button
                  type="button"
                  className="action-btn cancel-btn fa fa-close"
                  onClick={() => setShowInsertForm(false)}
                ></button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Domains Table */}
      {loading ? (
        <p>Loading domains...</p>
      ) : error ? (
        <p className="error">{error}</p>
      ) : filteredDomains.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Domain</th>
              <th>Description</th>
              <th>Icon</th>
              <th>Status</th>
              <th>Created</th>
              <th>Updated</th>
              {!isReadOnly && (
                <th>Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredDomains.map((domain, index) => (
              <tr key={domain.id}>
                <td>{index + 1}</td>
                {editDomain === domain.id ? (
                  <>
                    <td>
                      <input
                        type="text"
                        name="domain"
                        value={editedValues.domain}
                        onChange={handleEditChange}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="domainDesc"
                        value={editedValues.domainDesc}
                        onChange={handleEditChange}
                      />
                    </td>
                    {/* Edit icon */}
                    <td>
                      <input
                        type="text"
                        name="icon"
                        value={editedValues.icon}
                        onChange={handleEditChange}
                      />
                    </td>
                    <td>
                      <select
                        name="status"
                        value={editedValues.status}
                        onChange={handleEditChange}
                      >
                        <option value="A">Active</option>
                        <option value="I">Inactive</option>
                      </select>
                    </td>
                    <td>{new Date(domain.created).toLocaleString()}</td>
                    <td>{new Date(domain.updated).toLocaleString()}</td>
                    <td>
                      <button
                        className="icon-btn update-btn fa fa-edit"
                        onClick={handleUpdate}
                      ></button>
                      <button
                        className="icon-btn delete-btn fa fa-close"
                        onClick={() => setEditDomain(null)}
                      ></button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{domain.domain}</td>
                    <td>{domain.domainDesc}</td>
                    <td>{domain.icon || ""}</td>
                    <td>
                    {isReadOnly ? (
                      <span>{domain.status === "A" ? "Active" : "Inactive"}</span>
                    ) : (
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={domain.status === "A"}
                          onChange={() => toggleStatus(domain)}
                        />
                        <span className="slider"></span>
                      </label>
                    )}
                    </td>
                    <td>{new Date(domain.created).toLocaleString()}</td>
                    <td>{new Date(domain.updated).toLocaleString()}</td>
                      {!isReadOnly && (
                      <td>
                            <button
                              className="icon-btn update-btn fa fa-edit"
                              onClick={() => handleEdit(domain)}
                            ></button>
                            <button
                              className="icon-btn delete-btn fa fa-trash"
                              onClick={() => handleDelete(domain.id)}
                            ></button>
                      </td>
                      )}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No domains available.</p>
      )}
    </div>
  );
};

export default DomainsManager;
